import React, { useEffect, useState } from 'react';
import { gstRuleAPI } from '../../services/api';
import { TableSkeleton, EmptyState } from '../../components/skeletons';

const S = {
  page: { padding: 24, background: 'var(--cream)', minHeight: '100vh' },
  title: { fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 20 },
  card: { background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 10 },
  label: { fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'block' },
  input: { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, boxSizing: 'border-box' },
  btn: { background: '#1B5C3A', color: '#fff', border: 'none', padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  activeBadge: { display: 'inline-block', background: '#E8F5EE', color: '#1B5C3A', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, marginLeft: 8 },
  inactiveBadge: { display: 'inline-block', background: '#F3F4F6', color: 'var(--muted)', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, marginLeft: 8 },
};

const emptyRule = () => ({
  name: '', sacCode: '998361',
  sacDescription: 'Advertising services (lead-generation / marketplace facilitation)',
  totalRate: 18, cgstRate: 9, sgstRate: 9, igstRate: 18,
  platformState: 'gujarat', platformLegalName: 'Kanjusee',
  platformGstin: '', platformAddress: '', isActive: false, notes: '',
});

export default function GstRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    gstRuleAPI.list()
      .then(r => setRules(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const startNew = () => setEditing(emptyRule());
  const startEdit = (rule) => setEditing({ ...rule });
  const cancel = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    try {
      if (editing._id) await gstRuleAPI.update(editing._id, editing);
      else await gstRuleAPI.create(editing);
      setEditing(null);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const activate = async (id) => {
    try { await gstRuleAPI.activate(id); load(); }
    catch (err) { alert(err.response?.data?.message || 'Activate failed'); }
  };

  const field = (key, label, type = 'text', extra = {}) => (
    <div>
      <label style={S.label}>{label}</label>
      <input
        style={S.input}
        type={type}
        value={editing[key] ?? ''}
        onChange={e => setEditing({ ...editing, [key]: type === 'number' ? parseFloat(e.target.value || 0) : e.target.value })}
        {...extra}
      />
    </div>
  );

  return (
    <div style={S.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={S.title}>GST Rules</div>
          <div style={S.subtitle}>Only the active rule is applied to new invoices. Historical rules are kept for audit.</div>
        </div>
        {!editing && <button style={S.btn} onClick={startNew}>+ New Rule</button>}
      </div>

      {editing && (
        <div style={S.card}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>{editing._id ? 'Edit rule' : 'New rule'}</div>
          <div style={S.row}>
            {field('name', 'Rule name')}
            {field('sacCode', 'SAC code')}
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={S.label}>SAC description</label>
            <input style={S.input} value={editing.sacDescription ?? ''} onChange={e => setEditing({ ...editing, sacDescription: e.target.value })} />
          </div>
          <div style={S.row}>
            {field('totalRate', 'Total rate (%)', 'number', { min: 0, max: 100, step: 0.01 })}
            {field('platformState', 'Platform state', 'text')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 10 }}>
            {field('cgstRate', 'CGST (%)', 'number', { min: 0, max: 100, step: 0.01 })}
            {field('sgstRate', 'SGST (%)', 'number', { min: 0, max: 100, step: 0.01 })}
            {field('igstRate', 'IGST (%)', 'number', { min: 0, max: 100, step: 0.01 })}
          </div>
          <div style={S.row}>
            {field('platformLegalName', 'Legal name')}
            {field('platformGstin', 'Platform GSTIN')}
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={S.label}>Platform address</label>
            <input style={S.input} value={editing.platformAddress ?? ''} onChange={e => setEditing({ ...editing, platformAddress: e.target.value })} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={S.label}>Notes</label>
            <input style={S.input} value={editing.notes ?? ''} onChange={e => setEditing({ ...editing, notes: e.target.value })} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!editing.isActive} onChange={e => setEditing({ ...editing, isActive: e.target.checked })} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Set as active — deactivates any other active rule.</span>
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={S.btn} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save rule'}</button>
            <button style={{ ...S.btn, background: 'transparent', color: 'var(--char)', border: '1px solid var(--border)' }} onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={4} columns={5} />
      ) : rules.length === 0 && !editing ? (
        <EmptyState title="No GST Rules Yet" description="Create a rule to configure GST rates for invoice generation." />
      ) : (
        rules.map(rule => (
          <div key={rule._id} style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>
                  {rule.name}
                  {rule.isActive ? <span style={S.activeBadge}>ACTIVE</span> : <span style={S.inactiveBadge}>inactive</span>}
                </div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
                  SAC <strong>{rule.sacCode}</strong> · {rule.totalRate}% · CGST {rule.cgstRate}% / SGST {rule.sgstRate}% / IGST {rule.igstRate}% · Platform state: {rule.platformState}
                </div>
                {rule.sacDescription && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{rule.sacDescription}</div>}
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                  Effective from {new Date(rule.effectiveFrom || rule.createdAt).toLocaleDateString('en-IN')}
                  {rule.platformGstin ? ` · GSTIN ${rule.platformGstin}` : ' · GSTIN not set'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!rule.isActive && <button style={{ ...S.btn, background: '#F5A623', color: '#1A1A1A' }} onClick={() => activate(rule._id)}>Activate</button>}
                <button style={{ ...S.btn, background: 'transparent', color: '#1B5C3A', border: '1.5px solid #1B5C3A' }} onClick={() => startEdit(rule)}>Edit</button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
